import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { FiTrash } from 'react-icons/fi';

interface DragAndDropProps {
  onFilesUpload: (file: string) => void;
  onRemove: () => void;
  currentFile: string | undefined;
}

interface UploadResponse {
  s3_url: string;
  message?: string;
}

export const DragAndDrop: React.FC<DragAndDropProps> = ({
  onFilesUpload,
  onRemove,
  currentFile
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  const id = Math.random().toString(36).substring(7);

  const isImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    return validTypes.includes(file.type);
  };

  const validateFileSize = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    return file.size <= maxSize;
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError('');

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!isImageFile(file)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (!validateFileSize(file)) {
      setError('File size must be less than 5MB');
      return;
    }

    handleFile(file);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (!validateFileSize(file)) {
      setError('File size must be less than 5MB');
      return;
    }

    handleFile(file);
  };

  // File conversion and upload
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix to get just the base64 string
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (file: File) => {
    const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
    const fileExtension = file.name.split('.').pop();

    const newName = `${fileNameWithoutExtension}_${id}.${fileExtension}`;

    if (!file) return;

    setIsUploading(true);
    setError('');

    try {
      const base64 = await convertToBase64(file);

      // Create payload object
      const payload = {
        file_name: newName,
        content_type: file.type,
        file_data: base64
      };

      // Send to server
      const response = await axios.post<UploadResponse>(
        'https://e6w2t1d2f1.execute-api.us-east-1.amazonaws.com/dev/upload',
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 second timeout
        }
      );

      if (response.data && response.data.s3_url) {
        onFilesUpload(response.data.s3_url);
      } else {
        throw new Error('Invalid server response');
      }
    } catch (error) {
      console.error('Upload error:', error);

      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          setError('Upload timed out. Please try again.');
        } else if (error.response) {
          setError(`Upload failed: ${error.response.data.message || 'Please try again.'}`);
        } else if (error.request) {
          setError('Network error. Please check your internet connection.');
        } else {
          setError('Failed to upload image. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
    </div>
  );

  return (
    <div className="w-full">
      {currentFile ? (
        <div className="gap-4 flex items-center justify-center bg-gray-100 p-3 rounded-lg mb-2">
          <div>
            <p className="text-sm text-gray-800">{currentFile.split('/').pop()}</p>
          </div>
          <button
            onClick={onRemove}
            className="flex items-center text-red-500 hover:text-red-700 transition-colors"
            aria-label="Remove file"
          >
            <FiTrash size={18} />
          </button>
        </div>
      ) : (
        <div className="pb-4">
          <input
            id={`file-input-${id}`}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple={false}
            className="hidden"
            onChange={handleFileInput}
            disabled={isUploading}
          />
          <label
            htmlFor={`file-input-${id}`}
            className={`
              flex flex-col items-center justify-center
              w-full h-32
              border-2 border-dashed rounded-lg
              cursor-pointer
              transition-all duration-200
              ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
              ${isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              }
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <LoadingSpinner />
            ) : (
              <>
                <svg
                  className={`w-8 h-8 mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <div className="flex flex-col items-center space-y-1">
                  <p className={`text-sm font-medium ${isDragging ? 'text-blue-500' : 'text-gray-600'}`}>
                    {isDragging ? 'Drop image here' : 'Drag and drop an image here'}
                  </p>
                  <p className="text-xs text-gray-500">
                    or click to select (Max 5MB)
                  </p>
                </div>
              </>
            )}
          </label>
          {error && (
            <div className="mt-2 text-center">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DragAndDrop;